import React, { useEffect, useState } from "react";
import "./styles/dashboard.css";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import CommonHeader from "./components/CommonHeader";
import { hasPermission } from "./utils/permissionUtils";

const DriverDetails = () => {
  const navigate = useNavigate();

  const [pickupPoints, setPickupPoints] = useState([]);
  const [selectedPickupPoint, setSelectedPickupPoint] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [driverDetails, setDriverDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [editDriverDetails, setEditDriverDetails] = useState('');
  const [editBusDetails, setEditBusDetails] = useState('');
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkDriverDetails, setBulkDriverDetails] = useState({});
  const [bulkBusDetails, setBulkBusDetails] = useState({});

  useEffect(() => {
    fetchPickupPoints();
  }, []);

  const fetchPickupPoints = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = new URLSearchParams();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/Driver_details/getPickupPoints`, {
        method: 'POST',
        headers: {
          'Authorization': token
        },
        body: data
      });

      const result = await response.json();
      if (result.status) {
        setPickupPoints(result.pickup_points);
      } else {
        toast.error(result.message || 'Failed to fetch pickup points');
      }
    } catch (error) {
      console.error('Error fetching pickup points:', error);
      toast.error('Failed to fetch pickup points');
    }
  };

  const handleGenerateDriverDetails = async () => {
    if (!selectedPickupPoint || !fromDate || !toDate) {
      toast.error('Please select pickup point and date range');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const data = new URLSearchParams();
      data.append('pickup_location_fk', selectedPickupPoint);
      data.append('from_date', fromDate);
      data.append('to_date', toDate);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/Driver_details/getDriverDetailsList`, {
        method: 'POST',
        headers: {
          'Authorization': token
        },
        body: data
      });

      const result = await response.json();
      if (result.status) {
        setDriverDetails(result);
        // Initialize bulk details for editing
        const driverDetailsObj = {};
        const busDetailsObj = {};
        result.driver_details_list.forEach(booking => {
          driverDetailsObj[booking.booking_id] = booking.driver_details || '';
          busDetailsObj[booking.booking_id] = booking.bus_details || '';
        });
        setBulkDriverDetails(driverDetailsObj);
        setBulkBusDetails(busDetailsObj);
      } else {
        toast.error(result.message || 'Failed to generate driver details');
      }
    } catch (error) {
      console.error('Error generating driver details:', error);
      toast.error('Failed to generate driver details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDriverBusDetails = async (bookingId, driverDetails, busDetails) => {
    try {
      const token = localStorage.getItem('token');
      const data = new URLSearchParams();
      data.append('booking_id', bookingId);
      data.append('driver_details', driverDetails);
      data.append('bus_details', busDetails);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/Driver_details/updateDriverBusDetails`, {
        method: 'POST',
        headers: {
          'Authorization': token
        },
        body: data
      });

      const result = await response.json();
      if (result.status) {
        toast.success('Driver and bus details updated successfully');
        // Update local state
        setDriverDetails(prev => ({
          ...prev,
          driver_details_list: prev.driver_details_list.map(booking => 
            booking.booking_id === bookingId 
              ? { ...booking, driver_details: driverDetails, bus_details: busDetails }
              : booking
          )
        }));
        setEditingDriver(null);
        setEditDriverDetails('');
        setEditBusDetails('');
      } else {
        toast.error(result.message || 'Failed to update driver and bus details');
      }
    } catch (error) {
      console.error('Error updating driver and bus details:', error);
      toast.error('Failed to update driver and bus details');
    }
  };

  const handleBulkUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = new URLSearchParams();
      
      // Add bookings data as a JSON string
      const bookingsData = Object.keys(bulkDriverDetails).map(bookingId => ({
        booking_id: bookingId,
        driver_details: bulkDriverDetails[bookingId],
        bus_details: bulkBusDetails[bookingId]
      }));
      
      console.log('Sending bookings data:', bookingsData); // Debug log
      data.append('bookings', JSON.stringify(bookingsData));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/Driver_details/updateMultipleDriverBusDetails`, {
        method: 'POST',
        headers: {
          'Authorization': token
        },
        body: data
      });

      const result = await response.json();
      if (result.status) {
        toast.success(result.message);
        // Refresh driver details
        handleGenerateDriverDetails();
        setBulkEditMode(false);
      } else {
        toast.error(result.message || 'Failed to update driver and bus details');
      }
    } catch (error) {
      console.error('Error bulk updating driver and bus details:', error);
      toast.error('Failed to update driver and bus details');
    }
  };

  const handleGenerateExcel = async () => {
    if (!driverDetails) {
      toast.error('Please generate driver details first');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const data = new URLSearchParams();
      data.append('pickup_location_fk', selectedPickupPoint);
      data.append('from_date', fromDate);
      data.append('to_date', toDate);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/Driver_details/generateExcelDriverDetails`, {
        method: 'POST',
        headers: {
          'Authorization': token
        },
        body: data
      });

      const result = await response.json();
      if (result.status) {
        // Download the file
        const link = document.createElement('a');
        link.href = result.download_url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Excel file downloaded successfully');
      } else {
        toast.error(result.message || 'Failed to generate Excel file');
      }
    } catch (error) {
      console.error('Error generating Excel:', error);
      toast.error('Failed to generate Excel file');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatTime = (timeString) => {
    return timeString;
  };

  if (!hasPermission('driverdetails')) {
    return (
      <div className="dashboard">
        <CommonHeader />
        <div className="container mt-4">
          <div className="alert alert-danger">
            You don't have permission to access this page.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <CommonHeader />
      <div className="container mt-4">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h4>Driver Details</h4>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Pickup Point</label>
                    <select 
                      className="form-select" 
                      value={selectedPickupPoint} 
                      onChange={(e) => setSelectedPickupPoint(e.target.value)}
                    >
                      <option value="">Select Pickup Point</option>
                      {pickupPoints.map(point => (
                        <option key={point.pickup_point_id} value={point.pickup_point_id}>
                          {point.pickup_point_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">From Date</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={fromDate} 
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">To Date</label>
                    <input 
                      type="date" 
                      className="form-control" 
                      value={toDate} 
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">&nbsp;</label>
                    <div>
                      <button 
                        className="btn btn-primary me-2" 
                        onClick={handleGenerateDriverDetails}
                        disabled={loading}
                      >
                        {loading ? 'Generating...' : 'Generate Details'}
                      </button>
                      {driverDetails && (
                        <button 
                          className="btn btn-success" 
                          onClick={handleGenerateExcel}
                        >
                          Download Excel
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {driverDetails && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <div className="alert alert-info">
                        <strong>Pickup Point:</strong> {driverDetails.pickup_point_name} | 
                        <strong> Period:</strong> {formatDate(driverDetails.from_date)} to {formatDate(driverDetails.to_date)} | 
                        <strong> Total Bookings:</strong> {driverDetails.total_bookings} | 
                        <strong> Active Bookings:</strong> {driverDetails.active_bookings} | 
                        <strong> Cancelled Bookings:</strong> {driverDetails.cancelled_bookings} | 
                        <strong> Total Amount:</strong> ₹{driverDetails.total_amount?.toFixed(2)}
                      </div>
                    </div>
                  </div>
                )}

                {driverDetails && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <button 
                        className="btn btn-warning me-2" 
                        onClick={() => setBulkEditMode(!bulkEditMode)}
                      >
                        {bulkEditMode ? 'Cancel Bulk Edit' : 'Bulk Edit Details'}
                      </button>
                      {bulkEditMode && (
                        <button 
                          className="btn btn-success" 
                          onClick={handleBulkUpdate}
                        >
                          Save All Changes
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {driverDetails && driverDetails.driver_details_list && driverDetails.driver_details_list.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered">
                      <thead className="table-dark">
                        <tr>
                          <th>Sl No</th>
                          <th>Agency</th>
                          <th>Pax Name</th>
                          <th>Contact No</th>
                          <th>Pickup Date</th>
                          <th>Pickup Time</th>
                          <th>Pickup Point</th>
                          <th>Drop Point</th>
                          <th>Amount</th>
                          <th>Driver Details</th>
                          <th>Bus Details</th>
                          <th>Added By</th>
                          <th>Booking Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driverDetails.driver_details_list.map((booking, index) => (
                          <tr key={booking.booking_id}>
                            <td>{index + 1}</td>
                            <td>{booking.agency_name}</td>
                            <td>{booking.pax_name}</td>
                            <td>{booking.pax_contact_no}</td>
                            <td>{formatDate(booking.pickup_date)}</td>
                            <td>{booking.pickup_time}</td>
                            <td>{booking.pickup_point_name}</td>
                            <td>{booking.drop_point_name}</td>
                            <td style={{ fontWeight: 'bold', color: '#28a745' }}>₹{parseFloat(booking.amount || 0).toFixed(2)}</td>
                            <td style={{ minWidth: '200px' }}>
                              {bulkEditMode ? (
                                <textarea
                                  className="form-control form-control-sm"
                                  value={bulkDriverDetails[booking.booking_id] || ''}
                                  onChange={(e) => setBulkDriverDetails(prev => ({
                                    ...prev,
                                    [booking.booking_id]: e.target.value
                                  }))}
                                  rows="2"
                                  style={{ fontSize: '12px' }}
                                />
                              ) : editingDriver === booking.booking_id ? (
                                <div>
                                  <textarea
                                    className="form-control form-control-sm mb-2"
                                    value={editDriverDetails}
                                    onChange={(e) => setEditDriverDetails(e.target.value)}
                                    rows="2"
                                    placeholder="Driver details..."
                                    style={{ fontSize: '12px' }}
                                  />
                                  <div className="d-flex gap-1">
                                    <button
                                      className="btn btn-success btn-sm"
                                      onClick={() => handleUpdateDriverBusDetails(booking.booking_id, editDriverDetails, editBusDetails)}
                                      style={{ padding: '2px 6px', fontSize: '10px' }}
                                    >
                                      ✓
                                    </button>
                                    <button
                                      className="btn btn-danger btn-sm"
                                      onClick={() => {
                                        setEditingDriver(null);
                                        setEditDriverDetails('');
                                        setEditBusDetails('');
                                      }}
                                      style={{ padding: '2px 6px', fontSize: '10px' }}
                                    >
                                      ✕
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div style={{ fontSize: '12px', marginBottom: '5px' }}>
                                    {booking.driver_details || 'No driver details'}
                                  </div>
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => {
                                      setEditingDriver(booking.booking_id);
                                      setEditDriverDetails(booking.driver_details || '');
                                      setEditBusDetails(booking.bus_details || '');
                                    }}
                                    style={{ padding: '2px 8px', fontSize: '10px' }}
                                  >
                                    Edit
                                  </button>
                                </div>
                              )}
                            </td>
                            <td style={{ minWidth: '200px' }}>
                              {bulkEditMode ? (
                                <textarea
                                  className="form-control form-control-sm"
                                  value={bulkBusDetails[booking.booking_id] || ''}
                                  onChange={(e) => setBulkBusDetails(prev => ({
                                    ...prev,
                                    [booking.booking_id]: e.target.value
                                  }))}
                                  rows="2"
                                  style={{ fontSize: '12px' }}
                                />
                              ) : editingDriver === booking.booking_id ? (
                                <textarea
                                  className="form-control form-control-sm"
                                  value={editBusDetails}
                                  onChange={(e) => setEditBusDetails(e.target.value)}
                                  rows="2"
                                  placeholder="Bus details..."
                                  style={{ fontSize: '12px' }}
                                />
                              ) : (
                                <div style={{ fontSize: '12px' }}>
                                  {booking.bus_details || 'No bus details'}
                                </div>
                              )}
                            </td>
                            <td>{booking.added_user}</td>
                            <td>{formatDate(booking.booking_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : driverDetails && driverDetails.driver_details_list && driverDetails.driver_details_list.length === 0 ? (
                  <div className="alert alert-warning">
                    No bookings found for the selected criteria.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverDetails;
